import React from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icon } from '@liftango/ui-icons';
import { getAllTemplates } from '../templates/importTemplates';
import { downloadTemplateCsv } from '../templates/generateTemplateCsv';
import type { ImportFileType } from '../types';

interface FileTypeSelectionStepProps {
  onSelectType: (type: ImportFileType) => void;
}

const FileTypeSelectionStep: React.FC<FileTypeSelectionStepProps> = ({ onSelectType }) => {
  const { t } = useTranslation('mapDialogs');
  const templates = getAllTemplates();

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 3 }}>
        {t('importWizard.fileTypeStep.instruction')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {templates.map(template => (
          <Paper
            key={template.id}
            elevation={1}
            sx={{
              p: 2.5,
              cursor: 'pointer',
              border: '2px solid transparent',
              borderRadius: 2,
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: 3,
              },
            }}
            onClick={() => onSelectType(template.id)}
          >
            <Stack spacing={1.5} alignItems="center" textAlign="center">
              <Icon
                name={template.iconPath ? undefined : (template.iconName as any)}
                d={template.iconPath}
                size={36}
              />
              <Typography variant="subtitle1" fontWeight={600}>
                {t(template.labelKey)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                {t(template.descriptionKey)}
              </Typography>
              {template.sampleRows.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadTemplateCsv(template);
                  }}
                  startIcon={<Icon name="file-arrow-down" size={16} />}
                >
                  {t('importWizard.downloadTemplate')}
                </Button>
              )}
              {template.id === 'gtfs' && (
                <Typography variant="caption" color="text.secondary">
                  {t('importWizard.gtfsNote')}
                </Typography>
              )}
            </Stack>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default FileTypeSelectionStep;
